import React, { FocusEvent, KeyboardEvent, PointerEvent, ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Colors } from '../constants/colors';
import { ArrowDownIcon } from './icons/ArrowDownIcon';
import { scrollToTop } from '../helpers/scrollToTop';
import { ArrowRightDiagonal } from './icons/ArrowRightDiagonal';

export type ServiceLink = {
  to: string;
  label: string;
  /** Opens another site in a new tab instead of navigating within this one. */
  external?: boolean;
};

type ServicesMenuProps = {
  label: string;
  links: ServiceLink[];
};

/** Header dropdown grouping the services we offer next to the product. */
export const ServicesMenu = ({ label, links }: ServicesMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Hovering with a mouse opens the menu; the click that usually follows must not
  // close it again. Touch taps only toggle, emulated hover is ignored.
  const openedByHover = useRef(false);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      setOpen(false);
      buttonRef.current?.focus();
    }
  };

  const onPointerEnter = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    openedByHover.current = true;
    setOpen(true);
  };

  const onPointerLeave = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    openedByHover.current = false;
    setOpen(false);
  };

  const onToggle = () => {
    if (openedByHover.current) {
      openedByHover.current = false;
      setOpen(true);
      return;
    }
    setOpen((o) => !o);
  };

  // Tabbing out of the menu closes it. Focus going nowhere (a click on a link
  // that does not take focus) is left to the click itself.
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next && !ref.current?.contains(next)) {
      setOpen(false);
    }
  };

  return (
    <MenuWrapper ref={ref} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} onKeyDown={onKeyDown} onBlur={onBlur}>
      <ToggleButton ref={buttonRef} type="button" aria-haspopup="true" aria-expanded={open} onClick={onToggle}>
        {label}
        <ArrowDownIcon size={20} />
      </ToggleButton>
      <Dropdown open={open}>
        {links.map((link) => (
          <li key={link.to}>
            {link.external ? (
              <a href={link.to} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
                {link.label}
                <ArrowRightDiagonal color={Colors.primary} />
              </a>
            ) : (
              <Link
                to={link.to}
                onClick={() => {
                  setOpen(false);
                  scrollToTop();
                }}
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </Dropdown>
    </MenuWrapper>
  );
};

const MenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  color: ${Colors.primary};
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const Dropdown = styled.ul<{ open: boolean }>`
  display: ${({ open }) => (open ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  /* Padding instead of margin keeps the hover area continuous between the toggle and the list. */
  margin: 0;
  padding: 10px 0 0;
  list-style: none;
  z-index: 10;

  li {
    background: ${Colors.white};
    border-left: 1px solid ${Colors.border};
    border-right: 1px solid ${Colors.border};
  }

  li:first-child {
    border-top: 1px solid ${Colors.border};
  }

  li:last-child {
    border-bottom: 1px solid ${Colors.border};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  a {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0.6rem 1.2rem;
    color: ${Colors.primary};
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      background: ${Colors.lighterGrey};
    }
  }
`;
